import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader, RefreshCw, ShieldOff, Smartphone } from 'lucide-react';
import Sheet from '../../common/Sheet';
import { usePermission } from '../../../hooks/usePermission';
import { createPermissionString as perm } from '../../../types/permissions';
import { OfflineAdminService, REASON_MAX, REASON_MIN } from '../../../services/admin/offlineAdmin.service';
import type { OfflineDeviceDto, QuarantineItemDto } from '../../../dtos/offline/offlineAdmin.dto';
import { isApiError } from '../../../dtos/common/api.dto';

export const STALE_PENDING_MS = 12 * 3600 * 1000;

const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');
const errorText = (e: unknown, fallback: string) => (isApiError(e) && e.statusCode < 500 ? e.message : e instanceof Error && !isApiError(e) ? e.message : fallback);

/** Oldest unsynced work older than 12 h: amber (iOS can evict storage of unused apps). */
export const isStalePending = (d: OfflineDeviceDto, nowMs = Date.now()) =>
  d.pendingOps > 0 && Boolean(d.oldestPendingAt) && nowMs - Date.parse(d.oldestPendingAt as string) > STALE_PENDING_MS;

const DevicesList: React.FC<{ canRevoke: boolean }> = ({ canRevoke }) => {
  const [devices, setDevices] = useState<OfflineDeviceDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<OfflineDeviceDto | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setDevices(await OfflineAdminService.listDevices());
    } catch (e) {
      setError(errorText(e, 'Could not load devices'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const revoke = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      await OfflineAdminService.revokeDevice(confirm.userId, confirm.deviceId);
      setConfirm(null);
      await load();
    } catch (e) {
      setError(errorText(e, 'Could not revoke the device'));
    } finally {
      setBusy(false);
    }
  };

  if (error && !devices) return <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error} <button type="button" className="ml-2 underline" onClick={load}>Retry</button></div>;
  if (!devices) return <p role="status" className="flex items-center gap-2 p-3 text-sm text-gray-600"><Loader size={16} className="animate-spin" />Loading devices…</p>;
  return (
    <>
      {error && <p role="alert" className="mb-2 rounded-md bg-red-50 p-2 text-sm text-red-800">{error}</p>}
      {devices.length === 0 ? (
        <p className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">No phones or tablets are set up for offline mode.</p>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
          {devices.map((d) => {
            const stale = isStalePending(d);
            return (
              <li key={`${d.userId}:${d.deviceId}`} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
                <Smartphone size={18} className="shrink-0 text-gray-500" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-900">{d.userName || 'User'} · {d.label || d.platform}</div>
                  <div className="text-gray-600">
                    Last seen {when(d.lastSeenAt)} · {d.pendingOps} unsynced
                    {d.pendingOps > 0 && <span className={stale ? 'ml-1 font-semibold text-amber-700' : 'ml-1'} data-testid={stale ? 'stale-pending' : undefined}>· oldest {when(d.oldestPendingAt)}{stale ? ' (over 12 h)' : ''}</span>}
                  </div>
                </div>
                {d.revoked ? <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">Revoked</span>
                  : canRevoke && <button type="button" onClick={() => setConfirm(d)} className="flex min-h-[40px] items-center gap-1 rounded-md border border-red-300 px-3 font-medium text-red-800"><ShieldOff size={14} aria-hidden="true" />Revoke</button>}
              </li>
            );
          })}
        </ul>
      )}
      <Sheet open={Boolean(confirm)} onClose={() => !busy && setConfirm(null)} title="Revoke device" subtitle={confirm ? `${confirm.userName || 'User'} · ${confirm.label || confirm.platform}` : ''}
        footer={(
          <div className="flex gap-2">
            <button type="button" onClick={() => setConfirm(null)} disabled={busy} className="min-h-[48px] flex-1 rounded-lg border border-gray-300 font-medium text-gray-700">Cancel</button>
            <button type="button" onClick={revoke} disabled={busy} className="min-h-[48px] flex-1 rounded-lg bg-red-700 font-semibold text-white disabled:opacity-50">{busy ? 'Revoking…' : 'Revoke'}</button>
          </div>
        )}>
        <p className="text-sm text-gray-800">The user is signed out everywhere. The next time this device connects it hands over any sealed work and erases its offline data. Use this for a lost or stolen device.</p>
      </Sheet>
    </>
  );
};

const QuarantineList: React.FC = () => {
  const [items, setItems] = useState<QuarantineItemDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deciding, setDeciding] = useState<{ item: QuarantineItemDto; action: 'importQuarantine' | 'discardQuarantine' } | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setItems(await OfflineAdminService.listQuarantine());
    } catch (e) {
      setError(errorText(e, 'Could not load held items'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reasonOk = reason.trim().length >= REASON_MIN && reason.trim().length <= REASON_MAX;
  const decide = async () => {
    if (!deciding || !reasonOk) return;
    setBusy(true);
    setFormError(null);
    try {
      await OfflineAdminService.decideQuarantine(deciding.action, deciding.item.patientId, deciding.item.opId, reason);
      setDeciding(null);
      setReason('');
      await load();
    } catch (e) {
      setFormError(errorText(e, 'That did not work'));
    } finally {
      setBusy(false);
    }
  };

  if (error && !items) return <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error} <button type="button" className="ml-2 underline" onClick={load}>Retry</button></div>;
  if (!items) return <p role="status" className="flex items-center gap-2 p-3 text-sm text-gray-600"><Loader size={16} className="animate-spin" />Loading held items…</p>;
  const open = items.filter((i) => i.status === 'OPEN');
  return (
    <>
      {open.length === 0 ? (
        <p className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">Nothing is held for review.</p>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
          {open.map((i) => (
            <li key={i.opId} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
              <AlertTriangle size={18} className="shrink-0 text-amber-600" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-gray-900">{i.opType} · {i.patientName || 'Patient'}</div>
                <div className="text-gray-600">By {i.userName || 'user'} · captured {when(i.capturedAt)} · received {when(i.receivedAt)} · {i.reason}</div>
              </div>
              <button type="button" onClick={() => { setReason(''); setFormError(null); setDeciding({ item: i, action: 'importQuarantine' }); }} className="min-h-[40px] rounded-md bg-slate-800 px-3 font-semibold text-white">Import</button>
              <button type="button" onClick={() => { setReason(''); setFormError(null); setDeciding({ item: i, action: 'discardQuarantine' }); }} className="min-h-[40px] rounded-md border border-gray-300 px-3 text-gray-700">Discard</button>
            </li>
          ))}
        </ul>
      )}
      <Sheet open={Boolean(deciding)} onClose={() => !busy && setDeciding(null)} title={deciding?.action === 'importQuarantine' ? 'Import held item' : 'Discard held item'} subtitle={deciding ? `${deciding.item.opType} · ${deciding.item.patientName || 'Patient'}` : ''}
        footer={(
          <button type="button" onClick={decide} disabled={busy || !reasonOk} className="min-h-[48px] w-full rounded-lg bg-slate-800 font-semibold text-white disabled:opacity-50">
            {busy ? 'Saving…' : deciding?.action === 'importQuarantine' ? 'Import' : 'Discard'}
          </button>
        )}>
        <p className="mb-2 text-sm text-gray-700">{deciding?.action === 'importQuarantine' ? 'The item is applied with every business rule; the original clinician stays the author and you are recorded as the importer.' : 'The item is not applied. It stays in the audit record.'}</p>
        <label htmlFor="quarantine-reason" className="mb-1 block text-sm font-medium text-gray-700">Reason (required, {REASON_MIN}–{REASON_MAX} characters)</label>
        <textarea id="quarantine-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={REASON_MAX} rows={3} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base" />
        {formError && <p role="alert" className="mt-2 rounded-lg bg-red-50 p-2 text-sm text-red-800">{formError}</p>}
      </Sheet>
    </>
  );
};

/**
 * Admin → Devices (Phase 16C C11): offline phones and tablets with their unsynced work, Revoke,
 * and the Quarantine of synced items held for supervisor review. Each view is permission-gated.
 */
const AdminDevices: React.FC = () => {
  const canRead = usePermission(perm('admin:offline:devices:read'));
  const canRevoke = usePermission(perm('admin:offline:devices:revoke'));
  const canReview = usePermission(perm('admin:offline:quarantine:review'));
  const [view, setView] = useState<'devices' | 'quarantine'>(canRead ? 'devices' : 'quarantine');
  const [key, setKey] = useState(0);

  if (!canRead && !canReview) return <p className="p-4 text-sm text-gray-600">You don't have access to offline devices.</p>;
  const tabs = [...(canRead ? [{ id: 'devices' as const, label: 'Devices' }] : []), ...(canReview ? [{ id: 'quarantine' as const, label: 'Quarantine' }] : [])];
  const active = tabs.some((t) => t.id === view) ? view : tabs[0].id;
  return (
    <div className="space-y-3 bg-white p-4">
      <div className="flex flex-wrap items-center gap-2">
        <div role="tablist" aria-label="Offline devices" className="flex gap-1">
          {tabs.map((t) => (
            <button key={t.id} type="button" role="tab" aria-selected={active === t.id} onClick={() => setView(t.id)} className={`min-h-[40px] rounded-md px-3 text-sm font-medium ${active === t.id ? 'bg-slate-800 text-white' : 'text-slate-700 hover:bg-slate-50'}`}>{t.label}</button>
          ))}
        </div>
        <button type="button" onClick={() => setKey((k) => k + 1)} aria-label="Refresh" className="ml-auto flex min-h-[40px] min-w-[40px] items-center justify-center rounded-md border border-gray-300"><RefreshCw size={16} /></button>
      </div>
      <div role="tabpanel" key={`${active}-${key}`}>
        {active === 'devices' ? <DevicesList canRevoke={canRevoke} /> : <QuarantineList />}
      </div>
    </div>
  );
};

export default AdminDevices;
